<template>
  <v-card class="featured-chef-card elevation-4" :href="profileHref" @click.prevent="goToProfile">
    <!-- Photo Gallery Carousel with overlays -->
    <div class="carousel-wrapper">
      <v-carousel
        v-if="chefPhotos.length > 0"
        :show-arrows="chefPhotos.length > 1"
        hide-delimiters
        height="280px"
        cycle
        interval="5000"
      >
        <v-carousel-item
          v-for="(photo, i) in chefPhotos.slice(0, 3)"
          :key="i"
          :src="photo.photoPath + 'medium.' + photo.photoFilename"
          :alt="'Profile picture of ' + chef.profile.profileName"
        >
        </v-carousel-item>
      </v-carousel>
      <v-img
        v-else
        src="../assets/serving_400.webp"
        height="280px"
        width="100%"
        gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.3)"
        alt="Chef serving food background"
      ></v-img>

      <!-- Gradient overlay for better text visibility -->
      <div class="gradient-overlay"></div>

      <!-- Avatar and Name positioned at top left -->
      <div class="chef-info-overlay">
        <v-avatar 
          size="70" 
          class="elevation-6 avatar-border"
          v-if="chef.profile && chef.profile.photoPath && chef.profile.photoFilename"
        >
          <img
            width="70"
            height="70"
            style="object-fit: cover;"
            :alt="'Profile picture of ' + chef.profile.profileName"
            :src="chef.profile.photoPath + 'low.' + chef.profile.photoFilename"
            lazy-src="../assets/logo.png"
          />
        </v-avatar>
        <v-avatar 
          size="70" 
          class="elevation-6 avatar-border" 
          color="white"
          v-else
        >
          <v-icon size="45" color="primary">mdi-chef-hat</v-icon>
        </v-avatar>
        
        <!-- Chef Name next to avatar -->
        <div class="chef-name-text" v-if="chef.profile">
          {{ chef.profile.profileName }}
        </div>
      </div>

      <!-- Service Types Badges at bottom right -->
      <div class="services-badges" v-if="enabledServices.length > 0">
        <v-chip
          v-for="service in enabledServices"
          :key="service.serviceType.name"
          x-small
          color="white"
          class="service-chip"
          label
        >
          <span class="primary--text font-weight-bold">{{ service.serviceType.displayName }}</span>
        </v-chip>
      </div>

    </div>

    <v-card-text class="card-content px-4 py-3">
      <!-- Location -->
      <div class="location-section" v-if="chefLocation">
        <v-icon small color="primary" class="mr-1">mdi-map-marker</v-icon>
        <span class="text-body-2 font-weight-medium grey--text text--darken-2">{{ chefLocation }}</span>
      </div>
    </v-card-text>

    <v-card-actions class="px-4 pb-4 pt-0">
      <v-btn
        color="primary"
        block
        depressed
        rounded
        :href="profileHref"
        @click.stop.prevent="goToProfile"
        class="text-capitalize font-weight-bold"
        :aria-label="chef.profile ? 'View profile of ' + chef.profile.profileName : 'View Profile'"
      >
        <v-icon small left>mdi-eye</v-icon>
        View Profile
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "FeaturedChefCard",
  props: {
    chef: {
      type: Object,
      required: true
    }
  },
  computed: {
    chefPhotos() {
      if (this.chef && this.chef.photos) {
        return this.chef.photos.filter(photo => photo.photoPath && photo.photoFilename);
      }
      return [];
    },
    enabledServices() {
      if (this.chef && this.chef.service) {
        // Filter enabled services
        const enabled = this.chef.service.filter(service => service.enabled === true /*|| (service.packages && service.packages.some(p => p.enabled === true))*/);
        
        // Sort by the order in store.state.serviceType
        if (this.$store.state.serviceType && this.$store.state.serviceType.length > 0) {
          return enabled.sort((a, b) => {
            const indexA = this.$store.state.serviceType.findIndex(
              st => st.name === a.serviceType.name
            );
            const indexB = this.$store.state.serviceType.findIndex(
              st => st.name === b.serviceType.name
            );
            return indexA - indexB;
          });
        }
        
        return enabled;
      }
      return [];
    },
    chefLocation() {
      if (this.chef && this.chef.userDetails) {
        const city = this.chef.userDetails.city;
        const region = this.chef.userDetails.region;
        
        if (city && region) {
          return `${city}, ${region}`;
        } else if (city) {
          return city;
        }
      }
      return null; 
    },
    profileHref() {
      if (this.chef.profile) {
        const slug = this.chef.profile.profileNameNormalized || this.chef.profile.profileName;
        const lc = this.$route.params.language_country || 'en-AU';
        return `/${lc}/profile/${slug}`;
      }
      return null;
    },
    lowestPackagePrice() {
      if (this.chef && this.chef.service) {
        let allPackages = [];
        this.chef.service.forEach(service => {
          if (service.packages && service.packages.length > 0) {
            allPackages.push(...service.packages.filter(p => p.enabled));
          }
        });
        
        if (allPackages.length > 0) {
          let cheapest = allPackages.sort((a, b) => a.price - b.price)[0];
          return this.roundToXDigits(this.getPriceWithCommission(cheapest.price), 2);
        }
      }
      return null;
    }
  },
  methods: {
    goToProfile() {
      if (this.profileHref) {
        this.$router.push({ path: this.profileHref });
      }
    },
    getServiceIcon(serviceName) {
      const iconMap = {
        'CLASSIC': 'mdi-silverware-fork-knife',
        'SHARING': 'mdi-food-variant',
        'FUNCTION': 'mdi-glass-cocktail',
        'GRAZING': 'mdi-food-turkey',
        'FOOD_TRUCK': 'mdi-truck'
      };
      return iconMap[serviceName] || 'mdi-chef-hat';
    },
    roundToXDigits(value, digits) {
      return parseFloat(value.toFixed(digits));
    },
    getPriceWithCommission(price) {
      // Assuming commission is added to the price
      // You may need to adjust this based on your actual commission logic
      return price * 1.1; // Example: 10% commission
    }
  }
};
</script>

<style scoped>
.featured-chef-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
}

.featured-chef-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.2) !important;
}

.carousel-wrapper {
  position: relative;
  overflow: hidden;
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 40%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 50%, transparent 100%);
  z-index: 1;
  pointer-events: none;
}

.chef-info-overlay {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 2;
}

.avatar-border {
  border: 3px solid white !important;
  transition: transform 0.3s ease;
}

.featured-chef-card:hover .avatar-border {
  transform: scale(1.05);
}

.chef-name-text {
  color: white;
  font-weight: 700;
  font-size: 18px;
  text-shadow: 2px 2px 6px rgba(0, 0, 0, 0.8);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

.services-badges {
  position: absolute;
  bottom: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
  z-index: 2;
  max-width: 200px;
}

.service-chip {
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.95) !important;
  font-size: 11px !important;
  height: 24px !important;
  padding: 0 10px !important;
  transition: transform 0.2s ease;
}

.service-chip:hover {
  transform: scale(1.05);
}

.card-content {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.location-section {
  display: flex;
  align-items: center;
  padding: 4px 0;
}

.v-btn {
  letter-spacing: 0.5px;
  height: 44px !important;
  transition: all 0.3s ease;
}

.v-btn:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
